/**
 * One palette per kind of chart, so a reader learns a chart by its colours.
 *
 * Every chart on these pages draws exactly two series, and the two sit side by
 * side in one cluster. That makes the PAIR the thing that has to be legible,
 * which is what each set below is validated on: run
 * `node scripts/validate_palette.js "<a>,<b>" --mode light` and every pair here
 * clears the lightness band, the chroma floor, the colourblind separation
 * floor and contrast against the surface.
 *
 * Six hues checked against each other all-pairs would fail, and that is the
 * right outcome to ignore: the charts live in separate cards, so no reader ever
 * has to tell an Equipment bar from a Job Sites bar at a glance. What they do
 * have to tell apart is the two bars inside one cluster.
 *
 * The kinds:
 *
 *   Equipment  cool blues and greens. Out is leaving, In is returning.
 *   Job Sites  warm magenta and amber, deliberately unlike Equipment: a job
 *              opening is a different kind of event from a machine moving, and
 *              the two charts sit one above the other.
 *   Boomerangs orange and violet, the two problems. Orange is warm and
 *              alarming for a real trip that was wasted; violet is cool and
 *              desaturated because nothing actually moved.
 */

export type ChartSeries = { label: string; color: string };

export const ACTIVITY_COLORS = {
  out: '#2a78d6',
  in: '#1baf7a',
  jobsOpened: '#d55181',
  jobsClosed: '#eda100',
  boomerangReal: '#eb6834',
  boomerangArtifact: '#4a3aa7',
};

/** Equipment leaving and returning. Counts, never a rate. */
export const EQUIPMENT_SERIES: ChartSeries[] = [
  { label: 'Out', color: ACTIVITY_COLORS.out },
  { label: 'In', color: ACTIVITY_COLORS.in },
];

/** Same thing; kept as its own name for the hour-by-hour charts. */
export const EQUIPMENT_COUNT_SERIES = EQUIPMENT_SERIES;

/** Job sites starting and finishing. */
export const JOB_SITE_SERIES: ChartSeries[] = [
  { label: 'Opened', color: ACTIVITY_COLORS.jobsOpened },
  { label: 'Closed', color: ACTIVITY_COLORS.jobsClosed },
];

/**
 * The two boomerang classes. Never summed and never stacked: a return is an
 * operations problem and a cancellation is a data-entry one.
 */
export const BOOMERANG_SERIES: ChartSeries[] = [
  { label: 'Returns', color: ACTIVITY_COLORS.boomerangReal },
  { label: 'Cancellations', color: ACTIVITY_COLORS.boomerangArtifact },
];

export const BOOMERANG_LABELS = {
  real: 'Returns',
  artifact: 'Cancellations',
};
